import React, { useEffect, useState } from 'react';
import {
  CreditCard,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Loader,
  Coins,
  ChevronLeft,
  ChevronRight,
  Ticket,
  User,
  Mail,
  Shield,
  TrendingUp,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { billingApi, SubscriptionStatus } from '../services/billingApi';
import { creditApi, CreditBalance, CreditTransaction, CreditTransactionPage } from '../services/creditApi';

const WARNING_COLOURS: Record<string, string> = {
  NONE: 'text-emerald-600',
  LOW: 'text-amber-600',
  CRITICAL: 'text-red-600',
  DEPLETED: 'text-red-700',
};

const TYPE_LABELS: Record<string, { label: string; colour: string }> = {
  SUBSCRIPTION_GRANT: { label: 'Subscription', colour: 'text-emerald-600 bg-emerald-50' },
  SIGNUP_BONUS: { label: 'Bonus', colour: 'text-emerald-600 bg-emerald-50' },
  DEDUCTION: { label: 'Usage', colour: 'text-cyan-600 bg-cyan-50' },
  OPERATION_DEDUCTION: { label: 'Usage', colour: 'text-cyan-600 bg-cyan-50' },
  CONSUMPTION_CHARGE: { label: 'Consumption', colour: 'text-orange-600 bg-orange-50' },
  INVITE_GRANT: { label: 'Invite', colour: 'text-purple-600 bg-purple-50' },
  CANCELLATION_REFUND: { label: 'Refund', colour: 'text-teal-600 bg-teal-50' },
  ADMIN_ADJUSTMENT: { label: 'Adjustment', colour: 'text-medical-600 bg-medical-50' },
};

const CONSUMPTION_TIERS = [
  { range: '1 – 1,500', rate: '30p', discount: null },
  { range: '1,501 – 5,000', rate: '27p', discount: '10%' },
  { range: '5,001 – 10,000', rate: '24p', discount: '20%' },
  { range: '10,001+', rate: '21p', discount: '30%' },
];

export const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  // Credit state
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransactionPage | null>(null);
  const [txPage, setTxPage] = useState(0);

  // Invite code redemption
  const [inviteCode, setInviteCode] = useState('');
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeemError, setRedeemError] = useState('');

  useEffect(() => {
    loadSubscription();
    loadCredits();
  }, []);

  useEffect(() => {
    loadTransactions(txPage);
  }, [txPage]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError('');
      const sub = await billingApi.getSubscription();
      setSubscription(sub);
    } catch (err: any) {
      if (err.status === 404) {
        setError('No active subscription found');
      } else {
        setError(err.message || 'Failed to load subscription');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCredits = async () => {
    try {
      const balance = await creditApi.getBalance();
      setCreditBalance(balance);
    } catch (err) {
      console.error('[BillingPage] Failed to load credit balance:', err);
    }
  };

  const loadTransactions = async (page: number) => {
    try {
      const data = await creditApi.getTransactions(page, 10);
      setTransactions(data);
    } catch (err) {
      console.error('[BillingPage] Failed to load transactions:', err);
    }
  };

  const handleRedeemInviteCode = async () => {
    if (!inviteCode.trim()) return;
    setRedeemLoading(true);
    setRedeemError('');
    setRedeemMessage('');
    try {
      const result = await creditApi.redeemInviteCode(inviteCode.trim());
      setRedeemMessage(`${result.creditsGranted} pages added to your account!`);
      setInviteCode('');
      loadCredits();
      loadTransactions(0);
    } catch (err: any) {
      setRedeemError(err.message || 'Invalid invite code');
    } finally {
      setRedeemLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription? You will lose access at the end of your billing period.')) {
      return;
    }

    try {
      setCancelling(true);
      await billingApi.cancelSubscription();
      await loadSubscription();
    } catch (err: any) {
      alert(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      setOpeningPortal(true);
      const { portalUrl } = await billingApi.createPortalSession();
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
      setOpeningPortal(false);
    } catch (err: any) {
      alert(err.message || 'Failed to open billing portal');
      setOpeningPortal(false);
    }
  };

  const handleSubscribe = async () => {
    if (!user?.consentsCompleted) {
      navigate(`/signup`);
      return;
    }
    try {
      setSubscribing(true);
      const session = await billingApi.createCheckoutSession(user?.email || '');
      billingApi.redirectToCheckout(session.checkoutUrl);
    } catch (err: any) {
      alert(err.message || 'Failed to start checkout');
      setSubscribing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      active: {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckCircle className="h-4 w-4" />,
        label: 'Active'
      },
      incomplete: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: <AlertCircle className="h-4 w-4" />,
        label: 'Incomplete'
      },
      cancelled: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: <XCircle className="h-4 w-4" />,
        label: 'Cancelled'
      },
      trialing: {
        color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
        icon: <AlertCircle className="h-4 w-4" />,
        label: 'Trial'
      }
    };

    const config = statusConfig[status] || statusConfig.incomplete;

    return (
      <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium border ${config.color}`}>
        {config.icon}
        <span>{config.label}</span>
      </span>
    );
  };

  const formatCost = (pence: number) => `\u00A3${(pence / 100).toFixed(2)}`;

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center py-12">
            <Loader className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        </div>
      </div>
    );
  }

  const warningLevel = creditBalance?.warningLevel || 'NONE';

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-medical-900 dark:text-medical-100 mb-2">Account</h1>
          <p className="text-medical-600 dark:text-medical-400">Your profile, subscription, and billing information</p>
        </div>

        {/* Profile */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-slate-600" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">Profile</h2>
          </div>

          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="h-8 w-8 text-primary-600" />
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-medical-500 dark:text-medical-400">Name</p>
                <p className="font-medium text-medical-900 dark:text-medical-100">{user?.firstName} {user?.lastName}</p>
              </div>
              <div>
                <p className="text-sm text-medical-500 dark:text-medical-400">Email</p>
                <div className="flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5 text-medical-400" />
                  <p className="font-medium text-medical-900 dark:text-medical-100">{user?.email || '\u2014'}</p>
                </div>
              </div>
              {user?.department && (
                <div>
                  <p className="text-sm text-medical-500 dark:text-medical-400">Department</p>
                  <p className="font-medium text-medical-900 dark:text-medical-100">{user.department}</p>
                </div>
              )}
              {user?.gmcNumber && (
                <div>
                  <p className="text-sm text-medical-500 dark:text-medical-400">GMC Number</p>
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-3.5 w-3.5 text-medical-400" />
                    <p className="font-medium text-medical-900 dark:text-medical-100">{user.gmcNumber}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Usage & Balance */}
        {creditBalance && (
          <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Coins className={`h-5 w-5 ${WARNING_COLOURS[warningLevel]}`} />
              <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">Usage</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <p className="text-medical-500 dark:text-medical-400">Free Pages</p>
                <p className={`text-lg font-bold ${WARNING_COLOURS[warningLevel]}`}>
                  {creditBalance.freePageBalance}
                </p>
              </div>
              {creditBalance.hasActiveSubscription && (
                <>
                  <div>
                    <p className="text-medical-500 dark:text-medical-400">Pages This Month</p>
                    <p className="text-lg font-bold text-medical-900 dark:text-medical-100">
                      {creditBalance.monthlyConsumptionPages}
                    </p>
                  </div>
                  <div>
                    <p className="text-medical-500 dark:text-medical-400">Cost This Month</p>
                    <p className="text-lg font-bold text-medical-900 dark:text-medical-100">
                      {formatCost(creditBalance.monthlyConsumptionCostPence)}
                    </p>
                  </div>
                  <div>
                    <p className="text-medical-500 dark:text-medical-400">Current Rate</p>
                    <p className="text-lg font-bold text-medical-900 dark:text-medical-100">
                      {creditBalance.currentRatePence}p/page
                    </p>
                  </div>
                </>
              )}
              {!creditBalance.hasActiveSubscription && (
                <div>
                  <p className="text-medical-500 dark:text-medical-400">Lifetime Used</p>
                  <p className="text-lg font-bold text-medical-900 dark:text-medical-100">
                    {creditBalance.lifetimeUsed}
                  </p>
                </div>
              )}
            </div>

            {creditBalance.suspended && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-center justify-between">
                <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Your account is suspended due to a failed payment. Please update your payment method to continue processing.
                </p>
                <button
                  onClick={handleOpenPortal}
                  disabled={openingPortal}
                  className="ml-4 whitespace-nowrap px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors flex-shrink-0"
                >
                  {openingPortal ? 'Opening...' : 'Update Payment'}
                </button>
              </div>
            )}
            {!creditBalance.suspended && !creditBalance.hasActiveSubscription && warningLevel === 'DEPLETED' && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg flex items-center justify-between">
                <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                  You've used all your free pages. Subscribe to continue processing.
                </p>
                <button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="ml-4 whitespace-nowrap px-3 py-1.5 rounded bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors flex-shrink-0"
                >
                  Subscribe
                </button>
              </div>
            )}
          </div>
        )}

        {/* Redeem Access Code */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Ticket className="h-5 w-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">Redeem Access Code</h2>
          </div>
          <div className="flex gap-3">
            <input
              type="text"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="Enter access code"
              maxLength={20}
              className="input-field flex-1 uppercase tracking-widest font-mono"
            />
            <button
              onClick={handleRedeemInviteCode}
              disabled={redeemLoading || !inviteCode.trim()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {redeemLoading ? <Loader className="h-4 w-4 animate-spin" /> : 'Redeem'}
            </button>
          </div>
          {redeemMessage && (
            <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-2">{redeemMessage}</p>
          )}
          {redeemError && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{redeemError}</p>
          )}
        </div>

        {error && !subscription ? (
          /* No subscription — show single-plan subscribe card */
          <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-5 w-5 text-medical-600 dark:text-medical-400" />
              <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">Subscribe to ClinLexis</h2>
            </div>

            <div className="rounded-lg border-2 border-primary-200 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10 p-5 mb-5">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <p className="text-lg font-bold text-medical-900 dark:text-medical-100">ClinLexis</p>
                  <p className="text-sm text-medical-500 dark:text-medical-400">Monthly subscription</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-medical-900 dark:text-medical-100">
                    {'\u00A3'}24.99<span className="text-sm font-normal text-medical-500 dark:text-medical-400">/mo</span>
                  </p>
                  <p className="text-xs text-medical-500 dark:text-medical-400">+ VAT</p>
                </div>
              </div>

              <ul className="text-sm text-medical-700 dark:text-medical-300 space-y-1.5 mb-4">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  1,000 free pages on sign-up (one-time bonus)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  Full platform access including AI analysis
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  Pay-as-you-go page processing from 21p/page
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  Per-case VAT invoices on completion
                </li>
              </ul>

              {/* Consumption tier breakdown */}
              <div className="bg-white dark:bg-medical-800 rounded border border-medical-200 dark:border-medical-700 p-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <TrendingUp className="h-3.5 w-3.5 text-medical-500" />
                  <p className="text-xs font-semibold text-medical-600 dark:text-medical-400 uppercase tracking-wide">Consumption Pricing</p>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-medical-500 dark:text-medical-400">
                      <th className="text-left py-1 font-medium">Pages / month</th>
                      <th className="text-right py-1 font-medium">Rate</th>
                      <th className="text-right py-1 font-medium">Saving</th>
                    </tr>
                  </thead>
                  <tbody className="text-medical-700 dark:text-medical-300">
                    {CONSUMPTION_TIERS.map((tier, i) => (
                      <tr key={i} className="border-t border-medical-100 dark:border-medical-700/50">
                        <td className="py-1">{tier.range}</td>
                        <td className="py-1 text-right font-medium">{tier.rate}</td>
                        <td className="py-1 text-right">{tier.discount ? <span className="text-emerald-600">{tier.discount}</span> : '\u2014'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-medical-400 dark:text-medical-500 mt-1.5">Tiers reset on the 1st of each month. Free pages are consumed first.</p>
              </div>
            </div>

            <button
              onClick={handleSubscribe}
              disabled={subscribing}
              className="inline-flex items-center space-x-2 btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {subscribing ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Redirecting to payment...</span>
                </>
              ) : (
                <>
                  <span>Subscribe Now</span>
                  <ExternalLink className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        ) : subscription ? (
          <>
            {/* Current Plan */}
            <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100 mb-2">Current Plan</h2>
                  <p className="text-medical-600 dark:text-medical-400">Your active subscription details</p>
                </div>
                {getStatusBadge(subscription.status)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CreditCard className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm text-medical-500 dark:text-medical-400 mb-1">Plan</p>
                    <p className="font-medium text-medical-900 dark:text-medical-100">
                      ClinLexis &mdash; {'\u00A3'}24.99/mo + VAT
                    </p>
                  </div>
                </div>

                {subscription.currentPeriodEnd && (
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-sm text-medical-500 dark:text-medical-400 mb-1">Next Billing Date</p>
                      <p className="font-medium text-medical-900 dark:text-medical-100">
                        {formatDate(subscription.currentPeriodEnd)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {subscription.stripeCustomerId && (
                <div className="mt-6 pt-6 border-t border-medical-200 dark:border-medical-700">
                  <p className="text-xs text-medical-500 dark:text-medical-400">
                    Customer ID: {subscription.stripeCustomerId}
                  </p>
                  {subscription.stripeSubscriptionId && (
                    <p className="text-xs text-medical-500 dark:text-medical-400 mt-1">
                      Subscription ID: {subscription.stripeSubscriptionId}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
              <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100 mb-4">Manage Subscription</h2>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-medical-50 dark:bg-medical-700/30 rounded-lg">
                  <div>
                    <p className="font-medium text-medical-900 dark:text-medical-100 mb-1">Cancel Subscription</p>
                    <p className="text-sm text-medical-600 dark:text-medical-400">
                      You'll retain access until the end of your billing period
                    </p>
                  </div>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling || subscription.status === 'cancelled'}
                    className="btn-secondary text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cancelling ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin mr-2" />
                        Cancelling...
                      </>
                    ) : (
                      'Cancel Plan'
                    )}
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-medical-50 dark:bg-medical-700/30 rounded-lg">
                  <div>
                    <p className="font-medium text-medical-900 dark:text-medical-100 mb-1">Manage in Stripe</p>
                    <p className="text-sm text-medical-600 dark:text-medical-400">
                      Update payment method, view invoices, and more
                    </p>
                  </div>
                  <button
                    onClick={handleOpenPortal}
                    disabled={openingPortal}
                    className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {openingPortal ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Opening...</span>
                      </>
                    ) : (
                      <>
                        <span>Open Stripe</span>
                        <ExternalLink className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : null}

        {/* Transaction History */}
        {transactions && transactions.content.length > 0 && (
          <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100 mb-4">Page History</h2>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-medical-200 dark:border-medical-700">
                    <th className="text-left py-2 pr-4 text-medical-500 dark:text-medical-400 font-medium">Date</th>
                    <th className="text-left py-2 pr-4 text-medical-500 dark:text-medical-400 font-medium">Type</th>
                    <th className="text-left py-2 pr-4 text-medical-500 dark:text-medical-400 font-medium">Description</th>
                    <th className="text-right py-2 pr-4 text-medical-500 dark:text-medical-400 font-medium">Pages</th>
                    <th className="text-right py-2 pr-4 text-medical-500 dark:text-medical-400 font-medium">Cost</th>
                    <th className="text-right py-2 text-medical-500 dark:text-medical-400 font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Group deduction transactions by caseId into single rows
                    const isDeduction = (t: string) => t === 'DEDUCTION' || t === 'OPERATION_DEDUCTION' || t === 'CONSUMPTION_CHARGE';
                    type DisplayRow = {
                      key: string; date: string; type: string; label: string;
                      amount: number; balanceAfter: number; caseName?: string;
                      docCount?: number; costPence: number;
                    };
                    const rows: DisplayRow[] = [];
                    const caseGroups = new Map<string, { txns: CreditTransaction[] }>();

                    for (const tx of transactions.content) {
                      if (isDeduction(tx.type) && tx.caseId) {
                        const group = caseGroups.get(tx.caseId);
                        if (group) {
                          group.txns.push(tx);
                        } else {
                          caseGroups.set(tx.caseId, { txns: [tx] });
                        }
                      } else {
                        rows.push({
                          key: tx.id,
                          date: tx.createdAt,
                          type: tx.type,
                          label: tx.type,
                          amount: tx.amount,
                          balanceAfter: tx.balanceAfter,
                          caseName: tx.caseName,
                          costPence: tx.costPence || 0,
                        });
                      }
                    }

                    // Collapse each case group into a single row
                    const groupedRows: DisplayRow[] = [];
                    for (const [caseId, group] of caseGroups) {
                      const totalAmount = group.txns.reduce((sum, t) => sum + t.amount, 0);
                      const totalCost = group.txns.reduce((sum, t) => sum + (t.costPence || 0), 0);
                      const latestTx = group.txns[0];
                      groupedRows.push({
                        key: `case-${caseId}`,
                        date: latestTx.createdAt,
                        type: 'DEDUCTION',
                        label: 'DEDUCTION',
                        amount: totalAmount,
                        balanceAfter: latestTx.balanceAfter,
                        caseName: latestTx.caseName || group.txns.find(t => t.caseName)?.caseName,
                        docCount: group.txns.length,
                        costPence: totalCost,
                      });
                    }

                    // Merge grouped and ungrouped rows, sort newest first
                    const allRows = [...rows, ...groupedRows].sort(
                      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                    );

                    return allRows.map((row) => {
                      const typeInfo = TYPE_LABELS[row.type] || { label: row.type, colour: 'text-medical-600 bg-medical-50' };
                      const description = row.caseName
                        ? row.caseName + (row.docCount && row.docCount > 1 ? ` (${row.docCount} documents)` : '')
                        : row.type === 'DEDUCTION'
                          ? `${Math.abs(row.amount)} pages processed`
                          : typeInfo.label;
                      return (
                        <tr key={row.key} className="border-b border-medical-100 dark:border-medical-700/50">
                          <td className="py-2.5 pr-4 text-medical-700 dark:text-medical-300 whitespace-nowrap">
                            {formatDate(row.date)}
                          </td>
                          <td className="py-2.5 pr-4">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${typeInfo.colour}`}>
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="py-2.5 pr-4 text-medical-600 dark:text-medical-400 max-w-[250px] truncate">
                            {description}
                          </td>
                          <td className={`py-2.5 pr-4 text-right font-medium whitespace-nowrap ${
                            row.amount > 0 ? 'text-emerald-600' : 'text-red-600'
                          }`}>
                            {row.amount > 0 ? '+' : ''}{row.amount}
                          </td>
                          <td className="py-2.5 pr-4 text-right text-medical-600 dark:text-medical-400 whitespace-nowrap">
                            {row.costPence > 0 ? formatCost(row.costPence) : '\u2014'}
                          </td>
                          <td className="py-2.5 text-right text-medical-700 dark:text-medical-300 font-medium">
                            {row.balanceAfter}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {transactions.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-medical-100 dark:border-medical-700">
                <p className="text-xs text-medical-500 dark:text-medical-400">
                  Page {transactions.number + 1} of {transactions.totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTxPage(txPage - 1)}
                    disabled={txPage === 0}
                    className="p-1.5 rounded hover:bg-medical-100 dark:hover:bg-medical-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4 text-medical-600 dark:text-medical-400" />
                  </button>
                  <button
                    onClick={() => setTxPage(txPage + 1)}
                    disabled={txPage >= transactions.totalPages - 1}
                    className="p-1.5 rounded hover:bg-medical-100 dark:hover:bg-medical-700 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4 text-medical-600 dark:text-medical-400" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Help */}
        <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-700 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-cyan-800 dark:text-cyan-200">
              <p className="font-medium mb-1">Need help with billing?</p>
              <p>Contact our support team at <a href="mailto:billing@clinlexis.co.uk" className="underline">billing@clinlexis.co.uk</a></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
